/**
 * Pick a readable text color (#fff or #111) for a given background hex.
 * Uses W3C relative luminance — same formula browsers use for WCAG contrast.
 *
 * Pass any hex (#rrggbb / #rgb) or a CSS color the browser would understand
 * if document is available. Returns '#fff' for dark bgs, '#111' for light.
 *
 * Use this when a CTA button paints itself with `var(--primary-color)` and
 * the primary varies per tenant (e.g. light gold on Atelier vs dark blue on
 * default). Hardcoded `text-white` fails contrast on any light primary.
 */

/**
 * Resolve a CSS color string to a concrete `#rrggbb` where possible.
 * Handles `var(--token, fallback)` by reading the computed custom property
 * off :root (canvas can't see CSS custom properties, so var() must be resolved
 * here first), then falls back to the var's literal fallback, then a canvas
 * parse for keywords / rgb() / hsl().
 */
export const resolveCssColor = (input: string | undefined): string | undefined => {
  if (!input || typeof input !== 'string') return input ?? undefined;
  let value = input.trim();

  // Unwrap var(--token, fallback) — possibly nested — against :root.
  let guard = 0;
  while (value.startsWith('var(') && guard++ < 4) {
    const m = value.match(/^var\(\s*(--[\w-]+)\s*(?:,\s*([\s\S]+))?\)$/);
    if (!m) break;
    let resolved = '';
    if (typeof document !== 'undefined') {
      resolved = getComputedStyle(document.documentElement).getPropertyValue(m[1]).trim();
    }
    value = (resolved || (m[2] ? m[2].trim() : '')).trim();
    if (!value) return undefined;
  }

  if (value.startsWith('#')) return value;

  // Resolve keyword / rgb() / hsl() via canvas.
  if (typeof document !== 'undefined') {
    try {
      const ctx = document.createElement('canvas').getContext('2d');
      if (ctx) {
        ctx.fillStyle = '#000';
        ctx.fillStyle = value;
        const out = ctx.fillStyle as string;
        if (out.startsWith('#')) return out;
        // canvas returns rgb()/rgba() for some inputs — keep as-is for luminance()
        return out;
      }
    } catch {
      /* noop */
    }
  }
  return value;
};

/** Relative luminance (0–1) of a concrete color, or null if unparseable. */
export const colorLuminance = (color: string | undefined): number | null => {
  const resolved = resolveCssColor(color);
  if (!resolved) return null;
  let r: number, g: number, b: number;
  if (resolved.startsWith('#')) {
    let hex = resolved.slice(1);
    if (hex.length === 3) hex = hex.split('').map((c) => c + c).join('');
    if (hex.length !== 6) return null;
    r = parseInt(hex.slice(0, 2), 16) / 255;
    g = parseInt(hex.slice(2, 4), 16) / 255;
    b = parseInt(hex.slice(4, 6), 16) / 255;
  } else {
    const m = resolved.match(/(\d+(?:\.\d+)?)/g);
    if (!m || m.length < 3) return null;
    r = Number(m[0]) / 255; g = Number(m[1]) / 255; b = Number(m[2]) / 255;
  }
  const lin = (c: number) => (c <= 0.03928 ? c / 12.92 : Math.pow((c + 0.055) / 1.055, 2.4));
  return 0.2126 * lin(r) + 0.7152 * lin(g) + 0.0722 * lin(b);
};

/** WCAG contrast ratio between two concrete colors, or null if unparseable. */
export const contrastRatio = (a: string | undefined, b: string | undefined): number | null => {
  const la = colorLuminance(a);
  const lb = colorLuminance(b);
  if (la === null || lb === null) return null;
  const hi = Math.max(la, lb), lo = Math.min(la, lb);
  return (hi + 0.05) / (lo + 0.05);
};

export const readableTextColor = (bg: string | undefined, fallback: '#fff' | '#111' = '#fff'): '#fff' | '#111' => {
  const L = colorLuminance(bg);
  if (L === null) return fallback;
  // Threshold 0.18 matches the W3C "relative luminance midpoint" — picks dark
  // text whenever bg is light enough that white text would fail AA.
  return L > 0.18 ? '#111' : '#fff';
};

/**
 * Return `preferred` if it has acceptable contrast (≥ `min`, default 3:1)
 * against `bg`; otherwise fall back to an auto-picked readable color.
 * Use when a configured/ seeded text color may clash with the background
 * (e.g. a seeded white announcement-bar text on a light primary).
 */
export const ensureReadable = (
  preferred: string | undefined,
  bg: string | undefined,
  min = 3,
): string => {
  if (preferred) {
    const ratio = contrastRatio(preferred, bg);
    if (ratio === null || ratio >= min) return preferred;
  }
  return readableTextColor(bg);
};
